// Programmatic asset store for the Telegram Mini App on iOS: WKWebView's NSURLCache
// refuses to persist multi-MB bodies and Service Workers are unavailable in Telegram's
// webview, so immutable HTTP headers buy us nothing — every session re-downloads the
// atlases/maps/sounds. This keeps a `fetch → Blob → IndexedDB` store keyed by the EXACT
// request URL. Content-hashed names / `?v=N` query keys make invalidation free: a new
// build ships new URLs ⇒ store miss ⇒ fetch+store; the old entries are pruned by URL.
//
// Everything here is failure-tolerant: any IndexedDB error (private-mode Safari, blocked
// partitioned storage, quota) degrades to a transparent network pass-through. The app must
// NEVER break because of the store.

const DB_NAME = 'shot-assets';
const STORE = 'blobs';
const DB_VERSION = 1;
// Skip storing when free space is this tight — better to pass through to the network than
// to fail writes / evict something the OS considers more important.
const MIN_FREE_BYTES = 80 * 1024 * 1024;

interface StoredAsset {
  blob: Blob;
  storedAt: number;
  bytes: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  return dbPromise;
}

function idbGet(db: IDBDatabase, url: string): Promise<StoredAsset | null> {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(url);
      req.onsuccess = () => resolve((req.result as StoredAsset | undefined) ?? null);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

function idbPut(db: IDBDatabase, url: string, value: StoredAsset): Promise<void> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, url);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}

let persistTried = false;
function ensurePersist(): void {
  if (persistTried || typeof navigator === 'undefined') return;
  persistTried = true;
  try { void navigator.storage?.persist?.().catch(() => undefined); } catch { /* ignore */ }
}

async function hasRoom(bytes: number): Promise<boolean> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (!est) return true; // no estimate API → can't tell, allow the write
    const quota = est.quota ?? 0;
    if (quota === 0) return true; // unknown quota → allow
    const free = quota - (est.usage ?? 0);
    return free - bytes >= MIN_FREE_BYTES;
  } catch { return true; }
}

// Byte-metered network fetch → Blob. `onChunk` fires per streamed chunk so a loader can drive
// an honest MB-progress bar (mirrors the atlas byte-meter); falls back to res.blob() when the
// body isn't streamable, reporting the whole size once.
async function fetchBlob(url: string, onChunk?: (n: number) => void): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`asset fetch ${res.status}: ${url}`);
  const reader = res.body?.getReader?.();
  if (!reader) {
    const blob = await res.blob();
    onChunk?.(blob.size);
    return blob;
  }
  const type = res.headers.get('content-type') ?? '';
  const chunks: BlobPart[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    onChunk?.(value.length);
  }
  return new Blob(chunks, type ? { type } : undefined);
}

/**
 * Resolve an asset as a Blob: IndexedDB hit → return the stored blob (reporting its bytes
 * instantly, so a progress bar just jumps); miss → fetch (byte-metered), store best-effort,
 * return. Any store failure degrades to a plain network fetch — the store is never on the
 * critical path for correctness. `onChunk` reports downloaded/served bytes for progress.
 */
export async function getAsset(url: string, onChunk?: (n: number) => void): Promise<Blob> {
  const db = await openDb();
  if (db) {
    const hit = await idbGet(db, url);
    if (hit?.blob) { onChunk?.(hit.bytes || hit.blob.size); return hit.blob; }
  }
  const blob = await fetchBlob(url, onChunk);
  if (db) {
    ensurePersist();
    // fire-and-forget: the caller already has its blob; storing must never block or throw
    void hasRoom(blob.size).then((room) => {
      if (room) void idbPut(db, url, { blob, storedAt: Date.now(), bytes: blob.size });
    });
  }
  return blob;
}

/**
 * Resolve an asset as a decoded HTMLImageElement (store-backed blob → object URL → decode).
 * The object URL is revoked after decode: a decoded HTMLImageElement retains its bitmap, so
 * the element stays paintable (drawImage / background) while the transient URL is freed.
 */
export async function getAssetImage(url: string, onChunk?: (n: number) => void): Promise<HTMLImageElement> {
  const blob = await getAsset(url, onChunk);
  const objUrl = URL.createObjectURL(blob);
  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`asset decode failed: ${url}`));
      img.src = objUrl;
    });
    await img.decode().catch(() => undefined); // onload already fired — decode is a nicety
  } finally {
    URL.revokeObjectURL(objUrl);
  }
  return img;
}

/**
 * Drop every stored entry whose URL is NOT in `keepUrls`. Called once at battle-preload start
 * with the full manifest of current asset URLs, so a new build's fresh URLs supersede (and
 * evict) the previous build's stale ones with zero server round-trips.
 */
export async function pruneAssets(keepUrls: Iterable<string>): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const keep = new Set(keepUrls);
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        if (typeof cur.key === 'string' && !keep.has(cur.key)) cur.delete();
        cur.continue();
      };
      req.onerror = () => resolve();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}
